import {
  type ComponentProps,
  type ReactNode,
  type RefObject,
  useLayoutEffect,
} from "react"
import {
  FloatingPortal,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react"
import * as RechartsPrimitive from "recharts"
import type { TooltipContentProps } from "recharts"
import type {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent"

const TOOLTIP_OFFSET_PX = 12
const VIEWPORT_PADDING_PX = 8

export type ChartTooltipFloatingProps = Omit<
  ComponentProps<typeof RechartsPrimitive.Tooltip>,
  "portal" | "content" | "wrapperStyle" | "allowEscapeViewBox"
> & {
  /** The element the chart is rendered in; the tooltip is placed relative to it. */
  containerRef: RefObject<HTMLElement | null>
  /** Tooltip body, given recharts' active / payload / label. */
  content: (props: TooltipContentProps<ValueType, NameType>) => ReactNode
}

type FloatingBoxProps = {
  /** Hover point in viewport coordinates. */
  x: number
  y: number
  children: ReactNode
}

function FloatingBox(props: FloatingBoxProps) {
  const { x, y, children } = props
  const { refs, floatingStyles } = useFloating({
    open: true,
    placement: "right-start",
    middleware: [
      offset(TOOLTIP_OFFSET_PX),
      flip(),
      shift({ padding: VIEWPORT_PADDING_PX }),
    ],
  })

  useLayoutEffect(() => {
    refs.setReference({
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        x,
        y,
        top: y,
        left: x,
        right: x,
        bottom: y,
      }),
    })
  }, [refs, x, y])

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        className="pointer-events-none z-50"
        style={floatingStyles}
      >
        {children}
      </div>
    </FloatingPortal>
  )
}

/**
 * A recharts tooltip rendered through a portal, so it never adds overflow
 * to a scroll container and stays inside the viewport. Recharts applies no
 * positioning of its own when given a `portal`, so the anchor is computed
 * here from the container rect and the hover coordinate.
 */
function ChartTooltipFloating(props: ChartTooltipFloatingProps) {
  const { containerRef, content, ...tooltipProps } = props

  return (
    <RechartsPrimitive.Tooltip
      {...tooltipProps}
      portal={document.body}
      content={(tooltipContentProps) => {
        const { active, coordinate } = tooltipContentProps
        const rect = containerRef.current?.getBoundingClientRect()
        if (!active || !coordinate || !rect) return null

        return (
          <FloatingBox
            x={rect.left + (coordinate.x ?? 0)}
            y={rect.top + (coordinate.y ?? 0)}
          >
            {content(tooltipContentProps)}
          </FloatingBox>
        )
      }}
    />
  )
}

export default ChartTooltipFloating
